'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RefreshCw, Home, AlertTriangle, Bug } from 'lucide-react';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Global application error:', error);
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 min-h-screen">
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl shadow-xl border-red-200 dark:border-red-800">
            <CardContent className="p-12 text-center">
              {/* Error Icon */}
              <div className="mb-8">
                <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
                </div>
                <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full"></div>
              </div>

              {/* Error Message */}
              <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-gray-100">
                Critical Application Error
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                A critical error occurred that prevented the application from loading properly. 
                This issue has been logged and we&apos;re working to resolve it.
              </p>

              {/* Error Details (for development) */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-8 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-left">
                  <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center gap-2">
                    <Bug className="h-4 w-4" />
                    Error Details (Development Only)
                  </h3>
                  <code className="text-sm text-red-700 dark:text-red-300 break-all">
                    {error.message}
                  </code>
                  {error.digest && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                      Error ID: {error.digest}
                    </p>
                  )}
                  {error.stack && (
                    <details className="mt-3">
                      <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer">
                        Stack Trace
                      </summary>
                      <pre className="text-xs text-red-700 dark:text-red-300 mt-2 whitespace-pre-wrap">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <Button 
                  size="lg" 
                  onClick={reset}
                  className="bg-red-600 hover:bg-red-700 text-white shadow-lg"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Reload Application
                </Button>
                
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => window.location.href = '/'}
                  className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                >
                  <Home className="h-5 w-5 mr-2" />
                  Go to Homepage
                </Button>
              </div>

              {/* Help Text */}
              <div className="mt-8 pt-6 border-t border-red-200 dark:border-red-800">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  If this problem persists, please try refreshing the page or clearing your browser cache. 
                  We apologize for the inconvenience and are working to resolve this issue.
                </p>
                
                {/* Fallback Navigation */}
                <div className="mt-4 flex flex-wrap gap-4 justify-center text-sm">
                  <Link 
                    href="/" 
                    className="text-red-600 hover:underline"
                  >
                    Homepage
                  </Link>
                  <Link 
                    href="/calculator" 
                    className="text-red-600 hover:underline"
                  >
                    Calculator
                  </Link>
                  <Link 
                    href="/about" 
                    className="text-red-600 hover:underline"
                  >
                    About
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </body>
    </html>
  );
}